import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * A one-shot "Plus perks for your next game" pass, earned by watching a rewarded ad. Spent by the
 * next game the player creates (host perks apply lobby-wide, so the whole game goes ad-free);
 * practice rounds never touch it. Persisted so a pass earned right before the app closes isn't
 * lost. Deliberately not time-based — one pass, one created game, nothing to expire.
 */
object PlusPass {
    private var prefs: PlayerPreferences? = null
    private val mutablePending = MutableStateFlow(false)
    val pending: StateFlow<Boolean> = mutablePending.asStateFlow()

    /** Seed from persistence. Call once at startup (alongside [Achievements.init]). */
    fun init(playerPreferences: PlayerPreferences) {
        prefs = playerPreferences
        mutablePending.value = playerPreferences.getPlusPassPending()
    }

    /** Award a pass — the rewarded ad was watched to completion. */
    fun grant() {
        mutablePending.value = true
        prefs?.setPlusPassPending(true)
    }

    /** Spends the pending pass. Returns true when one was pending. */
    fun consume(): Boolean {
        if (!mutablePending.value) return false
        mutablePending.value = false
        prefs?.setPlusPassPending(false)
        return true
    }
}

/**
 * A one-game trial of a specific paid dice cosmetic, earned by watching a rewarded ad from the
 * store sheet a locked die/hat opened. Cheap by design: the cosmetic is written onto the player's
 * doc for the next game they create or join (in-game rendering never re-verifies entitlements),
 * then the trial clears — it "expires" by simply never being written again. One skin slot and one
 * hat slot; earning a new trial replaces the old one. Earned dice (Lucky/Creator/Training) are
 * never trialable — those are badges, not merchandise.
 */
object DiceTrial {
    private var prefs: PlayerPreferences? = null
    private val mutableSkinId = MutableStateFlow<String?>(null)
    private val mutableHatId = MutableStateFlow<String?>(null)
    val pendingSkinId: StateFlow<String?> = mutableSkinId.asStateFlow()
    val pendingHatId: StateFlow<String?> = mutableHatId.asStateFlow()

    /** Seed from persistence. Call once at startup (alongside [PlusPass.init]). */
    fun init(playerPreferences: PlayerPreferences) {
        prefs = playerPreferences
        mutableSkinId.value = playerPreferences.getDiceTrialSkinId()
        mutableHatId.value = playerPreferences.getDiceTrialHatId()
    }

    fun grantSkin(id: String) {
        mutableSkinId.value = id
        prefs?.setDiceTrialSkinId(id)
    }

    fun grantHat(id: String) {
        mutableHatId.value = id
        prefs?.setDiceTrialHatId(id)
    }

    /** Spends the pending skin trial. Returns the trialed id, or null when none was pending. */
    fun consumeSkin(): String? = mutableSkinId.value?.also {
        mutableSkinId.value = null
        prefs?.setDiceTrialSkinId(null)
    }

    /** Spends the pending hat trial. Returns the trialed id, or null when none was pending. */
    fun consumeHat(): String? = mutableHatId.value?.also {
        mutableHatId.value = null
        prefs?.setDiceTrialHatId(null)
    }
}

/** The rewarded ad that earns a [PlusPass] or a [DiceTrial]. Real on Android; unsupported elsewhere. */
expect object BankoRewardedAd {
    /** Whether this platform can show rewarded ads at all — gates the offer UI. */
    val isSupported: Boolean

    /**
     * True while a loaded ad is waiting to be shown. The offer button keys off this so a tap can
     * never race the network — it shows a loading state instead of failing.
     */
    val isReady: StateFlow<Boolean>

    /** Starts loading an ad so a later [show] is instant. Safe to call repeatedly. */
    fun preload()

    /**
     * Shows the rewarded ad. [onResult] fires with true once the reward is earned — or false when
     * no ad is ready, it fails to show, or the viewer closes it before earning the reward.
     */
    fun show(onResult: (rewarded: Boolean) -> Unit)
}
